import {
  AppBar,
  Paper,
  Tabs,
  Tab,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import * as React from 'react';
import { ResponsiveIconButton } from '../../components';
import {
  DailyTab,
  RequestsTab,
  ReservationsSettingsTab,
  NewReservationDialog,
} from './components';
import { SettingsService } from 'services';
import { Setting } from 'interfaces';

interface Props {}

export enum ReservationsTabList {
  DAILY = 'daily',
  REQUESTS = 'requests',
  SETTINGS = 'settings',
}

const RESERVATIONS_ENABLED_KEY = 'reservations_enabled';

const useStyles = makeStyles((theme) => ({
  appBar: { marginBottom: theme.spacing(2) },
}));

/**
 * Reservations top-level view. We use plain MUI Tabs with LOCAL state
 * instead of the shared AppTabs component because:
 *
 *  1. AppTabs is URL-hash driven (it does `window.location.hash` lookup
 *     and sets activeTab from it).
 *  2. Something elsewhere in the app intermittently clears or resets the
 *     URL hash. Whenever that fires, the AppTabs activeTab falls back to
 *     its first tab (Daily) and the user is bounced.
 *  3. The other places that use AppTabs (Settings, External Orders) only
 *     have ONE level of tabs and tolerate the bounce. We have nested
 *     tabs here, which makes the bounce far more visible / disruptive.
 *
 * Using local state means the URL doesn't deep-link to a specific tab,
 * which is a fine trade for an admin SPA.
 */
const tabFromHash = (): ReservationsTabList => {
  const h = (window.location.hash || '').replace('#', '');
  if (h === ReservationsTabList.REQUESTS) return ReservationsTabList.REQUESTS;
  if (h === ReservationsTabList.SETTINGS) return ReservationsTabList.SETTINGS;
  return ReservationsTabList.DAILY;
};

export const Reservations: React.FC<Props> = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState<ReservationsTabList>(
    tabFromHash(),
  );

  // Listen for the bell in the header pushing /reservations#requests
  React.useEffect(() => {
    const onHashChange = () => setActiveTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [newDialogOpen, setNewDialogOpen] = React.useState(false);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [enabledSetting, setEnabledSetting] = React.useState<Setting | null>(
    null,
  );
  const [toggleBusy, setToggleBusy] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadEnabledSetting();
  }, []);

  const enabled = enabledSetting ? enabledSetting.value !== '0' : true;

  const handleToggleEnabled = async () => {
    if (!enabledSetting || toggleBusy) return;
    setToggleBusy(true);
    const newValue = enabled ? '0' : '1';
    try {
      await SettingsService.editSetting(enabledSetting.id, { value: newValue });
      setEnabledSetting({ ...enabledSetting, value: newValue });
      setSnackbar(
        newValue === '1'
          ? 'Reservations enabled — website can accept bookings'
          : 'Reservations paused — website shows "not accepting bookings"',
      );
    } catch (e) {
      setSnackbar('Failed to update setting');
    } finally {
      setToggleBusy(false);
    }
  };

  return (
    <React.Fragment>
      <Paper
        style={{
          padding: 16,
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <ResponsiveIconButton
          onClick={() => setNewDialogOpen(true)}
          color="primary"
          icon={AddIcon}
        >
          New Reservation
        </ResponsiveIconButton>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggleEnabled}
              disabled={!enabledSetting || toggleBusy}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              {enabled ? 'Accepting bookings' : 'Bookings paused'}
            </Typography>
          }
        />
      </Paper>

      <AppBar
        position="static"
        color="default"
        elevation={1}
        className={classes.appBar}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v as ReservationsTabList)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
        >
          <Tab label="Daily" value={ReservationsTabList.DAILY} />
          <Tab label="Requests" value={ReservationsTabList.REQUESTS} />
          <Tab label="Settings" value={ReservationsTabList.SETTINGS} />
        </Tabs>
      </AppBar>

      {activeTab === ReservationsTabList.DAILY && (
        <DailyTab refreshKey={refreshTrigger} />
      )}
      {activeTab === ReservationsTabList.REQUESTS && (
        <RequestsTab
          refreshKey={refreshTrigger}
          onChanged={() => setRefreshTrigger((n) => n + 1)}
        />
      )}
      {activeTab === ReservationsTabList.SETTINGS && (
        <ReservationsSettingsTab />
      )}

      <NewReservationDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreated={() => {
          setNewDialogOpen(false);
          setRefreshTrigger((n) => n + 1);
          setSnackbar('Reservation created');
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </React.Fragment>
  );

  async function loadEnabledSetting() {
    try {
      const all = await SettingsService.getSettings();
      const row = all.find((s: Setting) => s.key === RESERVATIONS_ENABLED_KEY);
      if (row) setEnabledSetting(row);
    } catch {
      // ignore; toggle stays disabled
    }
  }
};
